import { FastFeedBuyersChart } from "@/components/fast-feed/FastFeedBuyersChart";
import { FastFeedBuyersTable } from "@/components/fast-feed/FastFeedBuyersTable";
import { FastFeedCumulativeBreakdownChart } from "@/components/fast-feed/FastFeedCumulativeBreakdownChart";
import { FastFeedEarningsChart } from "@/components/fast-feed/FastFeedEarningsChart";
import { FastFeedKpiStrip } from "@/components/fast-feed/FastFeedKpiStrip";
import { FastFeedPurchasesTable } from "@/components/fast-feed/FastFeedPurchasesTable";
import { FastFeedRevenueChart } from "@/components/fast-feed/FastFeedRevenueChart";
import { FastFeedTicketsChart } from "@/components/fast-feed/FastFeedTicketsChart";
import { FastFeedTopBuyersChart } from "@/components/fast-feed/FastFeedTopBuyersChart";
import { RangeButtons } from "@/components/ui";
import { useContextBarExtra } from "@/lib/context-bar-extra";
import type { Range } from "@/lib/search-params";
import { fastFeedBuyersQuery, fastFeedDailyQuery, fastFeedSummaryQuery } from "@/queries/fast-feed";
import { rootRoute } from "@/router";
import { useQuery } from "@tanstack/react-query";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";
import { z } from "zod";

const fastFeedSearchSchema = z.object({
  range: z.enum(["24h", "7d", "30d", "all"]).optional(),
});

export const fastFeedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fast-feed",
  validateSearch: (s) => fastFeedSearchSchema.parse(s),
  component: FastFeedPage,
});

function FastFeedPage() {
  const search = fastFeedRoute.useSearch();
  const navigate = useNavigate({ from: "/fast-feed" });
  const range = (search.range ?? "30d") as Range;

  const { setExtraRight } = useContextBarExtra();

  const setRange = useCallback(
    (r: string) => navigate({ search: (prev) => ({ ...prev, range: r as Range }), replace: true }),
    [navigate],
  );

  const { data: summary, isLoading: summaryLoading } = useQuery(fastFeedSummaryQuery);
  const { data: daily = [], isLoading: dailyLoading } = useQuery(fastFeedDailyQuery(range));
  const { data: buyers = [], isLoading: buyersLoading } = useQuery(fastFeedBuyersQuery(50));

  const loading = summaryLoading || dailyLoading;

  useEffect(() => {
    setExtraRight(<RangeButtons value={range} onChange={setRange} />);
    return () => setExtraRight(null);
  }, [range, setRange, setExtraRight]);

  return (
    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
      {/* KPI strip */}
      <FastFeedKpiStrip summary={summary} daily={daily} loading={loading} />

      {/* Full-width earnings chart with H/D/W granularity tabs */}
      <FastFeedEarningsChart />

      {/* 2×2 grid — daily trend charts */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FastFeedRevenueChart rows={daily} loading={loading} />
        <FastFeedCumulativeBreakdownChart rows={daily} loading={loading} />
        <FastFeedBuyersChart rows={daily} loading={loading} />
        <FastFeedTicketsChart rows={daily} loading={loading} />
      </div>

      {/* Top buyers chart (full width) */}
      <FastFeedTopBuyersChart rows={buyers} loading={buyersLoading} />

      {/* Info banner */}
      <div
        style={{
          padding: "10px 14px",
          background: "var(--color-bg-panel)",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius-panel)",
          fontSize: 12,
          color: "var(--color-text-muted)",
          lineHeight: 1.6,
        }}
      >
        <strong style={{ color: "var(--color-text-primary)" }}>What is Fast Feed?</strong> Fast Feed
        is a paid WebSocket subscription service on Arbitrum One that delivers the sequencer's
        ordered transaction queue 50–100 ms before the public mempool. Subscribers pay in USDG per
        round. Revenue follows the same 97% DAO Treasury / 3% Developer Guild split as PGA tip
        revenue.
      </div>

      {/* Top buyers leaderboard */}
      <FastFeedBuyersTable rows={buyers} loading={buyersLoading} />

      {/* All individual purchases */}
      <FastFeedPurchasesTable limit={100} />
    </div>
  );
}
